// SPDX-License-Identifier: AGPL-3.0-or-later
/**
 * Canonical custom extension URLs and standard FHIR extension URLs used by
 * intakeform Questionnaires and QuestionnaireResponses. Keep this file as
 * the single source of truth — every place that builds or reads an
 * Extension must import from here.
 */
export const ExtensionUrl = {
    /** Marks an `attachment` item as a signature capture (vs. generic file upload). */
    signature: 'https://intakeform.dev/fhir/StructureDefinition/signature',
    /** Human-readable validation message paired with a field-level constraint. */
    validationMessage: 'https://intakeform.dev/fhir/StructureDefinition/validation-message',
    /** Form-level cross-field contradiction rule. Complex extension. */
    contradictionRule: 'https://intakeform.dev/fhir/StructureDefinition/contradiction-rule',
    /** Freshness threshold in days; on Questionnaire (form default) or item (override). */
    freshnessDays: 'https://intakeform.dev/fhir/StructureDefinition/freshness-days',
    /** Name of a registered async validator to run for this item on submit. */
    asyncValidator: 'https://intakeform.dev/fhir/StructureDefinition/async-validator',
    /** Timestamp the patient answered a given item, used by the freshness pass. */
    answeredAt: 'https://intakeform.dev/fhir/StructureDefinition/answered-at',
    /** Reference to the magic-link row a QuestionnaireResponse was submitted through. */
    magicLinkId: 'https://intakeform.dev/fhir/StructureDefinition/magic-link-id',
    /** Standard FHIR regex extension. */
    regex: 'http://hl7.org/fhir/StructureDefinition/regex',
    /** Standard FHIR minValue extension. */
    minValue: 'http://hl7.org/fhir/StructureDefinition/minValue',
    /** Standard FHIR maxValue extension. */
    maxValue: 'http://hl7.org/fhir/StructureDefinition/maxValue',
    /**
     * Inline "why we ask" help text rendered under the field label.
     * Surfaces clinical/legal rationale for sensitive fields so patients
     * trust the form before answering.
     */
    helpText: 'https://intakeform.dev/fhir/StructureDefinition/help-text',
    /**
     * Opt-in flag on an email field to run server-side quality checks
     * (MX lookup + disposable-domain blocklist). Default for new fields
     * is true; existing fields without the extension are left alone so
     * older Questionnaires don't suddenly start flagging.
     *
     * `valueBoolean=true` activates the check, `false` explicitly opts
     * out. Reads against the `email-mx` async validator.
     */
    emailQuality: 'https://intakeform.dev/fhir/StructureDefinition/email-quality',
    /**
     * Marks a `string`-typed item as an address-autocomplete field
     * (in-7r5). `valueBoolean=true` activates the AddressField renderer +
     * Google Places autocomplete (with a plain-text fallback when no API
     * key is configured). The answer payload is JSON-serialised into the
     * answer's `valueString`; see `packages/ui/src/renderer/fields/AddressField.tsx`.
     */
    address: 'https://intakeform.dev/fhir/StructureDefinition/address',
    /**
     * Optional country bias for an address-autocomplete field (in-7r5).
     * Stored as an ISO 3166-1 alpha-2 code (e.g. `US`, `CA`, `GB`) in
     * `valueCode`. Used as the `componentRestrictions.country` filter
     * when Google Places autocomplete is active.
     */
    addressCountry: 'https://intakeform.dev/fhir/StructureDefinition/address-country',
    /**
     * Opt-in flag on a string field to run libphonenumber-js parsing +
     * validation (in-apm). When set, the field-pass validates the answer
     * via parsePhoneNumberFromString and the Phone renderer takes over
     * for that field. Pairs with `phoneCountryBias` for the parse
     * default and the optional Twilio Lookup async validator.
     */
    phoneValidation: 'https://intakeform.dev/fhir/StructureDefinition/phone-validation',
    /**
     * Field-level default country code (ISO 3166-1 alpha-2, e.g. 'US',
     * 'GB') for phone parsing. Falls back to the org's `default_country`
     * setting, then to 'US'. Stored as `valueCode`.
     */
    phoneCountryBias: 'https://intakeform.dev/fhir/StructureDefinition/phone-country-bias',
    /**
     * Marks a `date` item as the patient's date of birth (in-3uo). Trips
     * extra validation (`runDobChecks`) — reject future dates with
     * severity error, warn at age > 120 — and is the signal used by the
     * API on submit to compute `subject_age_at_submit` on the response
     * row.
     *
     * Encoded as `valueString='date-of-birth'` so future modes (e.g.
     * `appointment-date`) can ride on the same extension URL.
     */
    dateMode: 'https://intakeform.dev/fhir/StructureDefinition/date-mode',
    /**
     * Per-item EMR prefill source. Item-level extension declaring where
     * the autofill flow should pull this item's answer from. Phase 1 uses
     * a linkId-convention map and does not read this extension; Phase 2
     * will surface it in the builder and consume it in the prefill
     * mapping layer. Reserved URL so persisted Questionnaires that
     * already carry it round-trip cleanly.
     */
    prefillSource: 'https://intakeform.dev/fhir/StructureDefinition/prefill-source',
    /**
     * Per-item translation (bead kjt). Carries one or more language
     * variants for a translatable field on the item (text, helpText, an
     * answerOption display, etc.). The extension is complex — it has
     * three sub-extensions: `field` (which target the translation
     * applies to), `lang` (BCP-47 code), `content` (translated string).
     * Multiple translation extensions on a single item are allowed —
     * one per (field, lang) pair. The patient renderer reads them via
     * `translateOrFallback` and falls back to the canonical English
     * value when a language has no entry.
     *
     * We use a project-local URL rather than the standard FHIR
     * `http://hl7.org/fhir/StructureDefinition/translation` because that
     * URL is defined on the primitive's `_text` shadow element, which
     * fragments storage across two parallel structures. A single
     * extension array on the item keeps the data legible and lets one
     * extension cover multiple translatable fields per language.
     */
    translation: 'https://intakeform.dev/fhir/StructureDefinition/translation',
    /**
     * Marks an `attachment` item as an insurance-card upload (bead
     * 2pc). When present, the patient renderer triggers an OCR pass
     * once the file finishes uploading and prefills the linked items
     * via `insuranceCardTarget`. The extension is `valueBoolean=true`;
     * any other value means "regular attachment, don't OCR."
     */
    insuranceCard: 'https://intakeform.dev/fhir/StructureDefinition/insurance-card',
    /**
     * On a dependent item, names which OCR-parsed field should populate
     * it. `valueCode` is one of `'payor'`, `'memberId'`, `'groupNumber'`.
     * The renderer matches dependent items to the upload by document
     * order — every item between two insurance-card uploads is owned by
     * the first card. Allows two-card flows (front + back, or primary
     * + secondary) without re-extending the type.
     */
    insuranceCardTarget: 'https://intakeform.dev/fhir/StructureDefinition/insurance-card-target',
    /**
     * Per-form magic-link TTL override in seconds (intakeform-preview-xv2).
     * Lives on the Questionnaire (not on individual items) — it's a
     * form-level property. Resolution order at issuance time:
     * per-link override > this extension > org settings > env default.
     * Bounds: 60s minimum, 30d maximum. Stored as `valueInteger`.
     */
    linkTtlSeconds: 'https://intakeform.dev/fhir/StructureDefinition/link-ttl-seconds',
    /**
     * Who is expected to complete an item or group (intakeform-preview-swwo).
     * Official forms often mix a parent section with a clinician section on
     * one document; this lets a consumer such as check-in show each person
     * only their part. `valueCode` is one of `CompletedBy`. It is an item-level
     * counterpart to SDC's Questionnaire-level `performerType` and uses the
     * same FHIR resource-type codes, plus `Teacher`, an intakeform code (not
     * a FHIR resource type) used where an official form is completed by the
     * child's teacher, school staff outside the care team. It applies to every
     * descendant; an item without it, and with no ancestor carrying it, is for
     * whoever fills in the form.
     */
    completedBy: 'https://intakeform.dev/fhir/StructureDefinition/completed-by',
    /**
     * The official document a library template reproduces
     * (intakeform-preview-swwo). On the Questionnaire. `valueAttachment` with
     * `url` (the issuing body's own copy), `title` (the document's name and
     * revision as printed on it) and `creation` (when we checked the source).
     * Recorded so a reviewer can diff the template against the source, and so
     * stale school-year revisions are easy to find.
     */
    sourceDocument: 'https://intakeform.dev/fhir/StructureDefinition/source-document',
    /**
     * Standard FHIR R4 `ordinalValue`: the numeric weight of an answer option,
     * on `answerOption.extension` (`valueDecimal`). A screener's "Several days"
     * scores 1; the weight is not shown to the person answering. FHIRPath's
     * `weight()` reads it in a `calculatedExpression`.
     */
    ordinalValue: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
    /**
     * Standard FHIR SDC `calculatedExpression`: the item's answer is computed
     * from other answers (e.g. a screener's total score), never typed.
     * `valueExpression` with `language: 'text/fhirpath'`, evaluated against the
     * QuestionnaireResponse with the Questionnaire as `%questionnaire` (see
     * `packages/validation/src/calculation.ts`). The renderer shows the value
     * read-only and the API recomputes it on submit, so a client-supplied
     * value never survives.
     */
    calculatedExpression: 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-calculatedExpression',
};
/**
 * Values accepted by the `completed-by` extension. The first three are FHIR
 * resource types, as in SDC `performerType`; `RelatedPerson` is a parent or
 * guardian. `Teacher` is an intakeform code, not a FHIR resource type: it
 * marks a section of an official form completed by the child's teacher,
 * school staff outside the care team (e.g. the NICHQ Vanderbilt teacher
 * scales).
 */
export const CompletedBy = {
    patient: 'Patient',
    relatedPerson: 'RelatedPerson',
    practitioner: 'Practitioner',
    teacher: 'Teacher',
};
/**
 * Sub-extension URLs inside a `translation` extension. The patient
 * renderer joins on (field, lang) to find the right entry.
 */
export const TranslationField = {
    /** The item field this entry translates: `text`, `helpText`, or
     * `answerOption:<key>` where key is the answerOptionKey of the
     * option being translated. */
    field: 'field',
    /** BCP-47 language tag (`es`, `vi`, `zh-Hans`, etc.). */
    lang: 'lang',
    /** The translated string. */
    content: 'content',
};
/** Sub-extension URLs inside `contradiction-rule` (kept stable for downstream consumers). */
export const ContradictionRuleField = {
    code: 'code',
    message: 'message',
    when: 'when',
    /**
     * Optional. `'patient'` makes the contradiction visible to the patient
     * inline (pre-submit, via the structural/form-level partition) so they
     * can fix it before submitting. `'clinician-only'` (the default) keeps
     * the rule server-only — used for sensitive eligibility logic that
     * shouldn't be reverse-engineered from the patient's browser.
     */
    visibility: 'visibility',
    /**
     * Optional short name for the rule, shown to staff where a flag's reason is
     * listed ("Flagged because: PHQ-9: self-harm answer") instead of the
     * generic `contradiction` code. The full `message` still appears with the
     * findings.
     */
    label: 'label',
};
export const CONTRADICTION_VISIBILITY_DEFAULT = 'clinician-only';
//# sourceMappingURL=extensions.js.map